<script context="module" lang="ts">
  import { setIconBasePath } from "@brave/leo/src/components/icon/icon.svelte";
  import "@fontsource-variable/inter";
  import "@fontsource/poppins/400.css";
  import "@fontsource/poppins/600.css";
  import { asset } from "$app/paths";
  import { writable } from "svelte/store";
  import "../css/app.css";

  export const isFetching = writable(false);

  setIconBasePath(asset('/nala-icons'));
</script>

<script lang="ts">
  import { browser } from "$app/environment";
  import { afterNavigate } from "$app/navigation";
  import Loading from "$components/Loading.svelte";
  import { getLocale } from "$lib/paraglide/runtime";
  import AlertCenter from "@brave/leo/src/components/alert/alertCenter.svelte";
  import DOMPurify from "isomorphic-dompurify";
  import { fade } from "svelte/transition";
  import { MATOMO_ID } from "$lib/Env";

  const matomoOrigin = "https://analytics.brave.com";
  let matomoPolicy: any;
  if (browser) {
    // @ts-ignore
    if (typeof window.trustedTypes == "undefined") {
      // @ts-ignore
      window.trustedTypes = { createPolicy: (n, rules) => rules };
    }

    // @ts-ignore
    matomoPolicy = trustedTypes.createPolicy("matomo-policy", {
      createScriptURL: (url: string) => {
        const trustedURL = new URL(url, matomoOrigin);
        if (trustedURL.origin === matomoOrigin) {
          return trustedURL;
        }
        // e.g. if url = "//mali.cio.us" or "https://ev.il" or "javascript://blah"
        throw new TypeError();
      },
    });

    // Global DOMPurify hooks to preserve target attribute only if rel="noopener noreferrer" is present
    const TEMP_TARGET_ATTR = 'data-temp-target';
    DOMPurify.addHook('beforeSanitizeAttributes', (node) => {
      if (node.tagName === 'A' && node.hasAttribute('target')) {
        node.setAttribute(TEMP_TARGET_ATTR, node.getAttribute('target'));
      }
    });
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName === 'A' && node.hasAttribute(TEMP_TARGET_ATTR)) {
        const rel = node.getAttribute('rel');
        if (rel === 'noopener noreferrer' || rel === 'noreferrer noopener') {
          node.setAttribute('target', node.getAttribute(TEMP_TARGET_ATTR));
        }
        node.removeAttribute(TEMP_TARGET_ATTR);
      }
    });
  }

  /**
   * Matomo analytics
   */
  let _paq = [];
  if (browser && MATOMO_ID) {
    // @ts-ignore
    _paq = window._paq = window._paq || [];
    _paq.push(["setTrackerUrl", matomoOrigin + "/matomo.php"]);
    _paq.push(["setSiteId", MATOMO_ID]);
    _paq.push(["disableCookies"]);
    _paq.push(["setSecureCookie", true]);
    _paq.push(["setCustomDimension", 1, navigator.language]);
    _paq.push(["setCustomDimension", 2, navigator.language]);
    const d = document,
      g = d.createElement("script"),
      s = d.getElementsByTagName("script")[0];
    g.async = true;
    g.src = matomoPolicy.createScriptURL("matomo.js");
    s?.parentNode?.insertBefore(g, s);
  }

  afterNavigate((navigation) => {
    // @ts-ignore
    _paq = window._paq = window._paq || [];
    _paq.push(["setReferrerUrl", navigation.from?.url.href ?? document.referrer ?? ""]);
    _paq.push(["setCustomUrl", navigation.to?.url.href]);
    _paq.push(["trackPageView"]);
    _paq.push(["enableLinkTracking"]);
  });

  $: {
    browser && document.documentElement.setAttribute("lang", getLocale());
  }
</script>

<svelte:head>
  <link rel="preconnect" href={matomoOrigin} />
</svelte:head>

<AlertCenter />

{#if $isFetching}
  <div
    out:fade={{ duration: 200 }}
    class="fixed left-0 top-0 z-[100] flex h-screen w-screen justify-center bg-container-background py-[30vh] md:bg-page-background"
  >
    <Loading />
  </div>
{/if}

<slot />
